import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const configNode = document.getElementById('spark-internet-cart-config');
let config = null;

try {
  config = configNode ? JSON.parse(configNode.textContent || '{}') : null;
} catch (error) {
  console.error('[SPARK Internet] Invalid cart configuration', error);
}

const planVariantIds = new Set((config?.planVariantIds || []).filter(Boolean).map(String));
const activationVariantId = config?.activationVariantId ? String(config.activationVariantId) : '';
let currentReconcile = null;
let rerunRequested = false;
let checkoutBypass = false;

function cartRoute(suffix = '') {
  const base = globalThis.Theme?.routes?.cart_url || '/cart';
  return `${base}${suffix}`;
}

function route(name, fallback) {
  return globalThis.Theme?.routes?.[name] || fallback;
}

async function fetchCart() {
  const response = await fetch(cartRoute('.json'), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
  return response.json();
}

function requiredActivationQuantity(cart) {
  return (cart.items || []).reduce((total, item) => {
    return planVariantIds.has(String(item.variant_id)) ? total + Number(item.quantity || 0) : total;
  }, 0);
}

function activationLines(cart) {
  return (cart.items || []).filter((item) => String(item.variant_id) === activationVariantId);
}

function lockActivationRows() {
  if (!activationVariantId) return;
  const selectors = document.querySelectorAll(
    `cart-quantity-selector-component[data-variant-id="${CSS.escape(activationVariantId)}"], quantity-selector-component[data-variant-id="${CSS.escape(activationVariantId)}"]`
  );

  for (const selector of selectors) {
    const row = selector.closest('.cart-items__table-row');
    if (!row) continue;
    row.classList.add('spark-internet-fee-line');

    const title = row.querySelector('a.cart-items__title');
    if (title) {
      title.removeAttribute('href');
      title.setAttribute('aria-disabled', 'true');
      title.setAttribute('tabindex', '-1');
    }

    const quantityCell = row.querySelector('.cart-items__quantity');
    if (quantityCell && !quantityCell.querySelector('.spark-internet-fee-required')) {
      const label = document.createElement('span');
      label.className = 'spark-internet-fee-required';
      label.textContent = 'Required one-time activation';
      quantityCell.append(label);
    }
  }
}

function applyCartUi(cart) {
  const hasInternet = requiredActivationQuantity(cart) > 0;
  document.documentElement.classList.toggle('spark-cart-has-internet', hasInternet);
  requestAnimationFrame(() => requestAnimationFrame(lockActivationRows));
}

async function changeLine(key, quantity) {
  const response = await fetch(route('cart_change_url', '/cart/change.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ id: key, quantity }),
  });
  if (!response.ok) throw new Error(`Cart line update failed: ${response.status}`);
  const data = await response.json();
  if (data.errors) throw new Error(typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors));
  return data;
}

async function addActivation(quantity) {
  const response = await fetch(route('cart_add_url', '/cart/add.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      items: [
        {
          id: Number(activationVariantId),
          quantity,
          properties: { _spark_component: 'internet_activation' },
        },
      ],
    }),
  });
  if (!response.ok) throw new Error(`Activation fee add failed: ${response.status}`);
  const data = await response.json();
  if (data.status) throw new Error(data.description || data.message || 'Activation fee add failed');
  return data;
}

async function performReconcile() {
  if (!activationVariantId || planVariantIds.size === 0) return null;

  const cart = await fetchCart();
  applyCartUi(cart);

  const required = requiredActivationQuantity(cart);
  const feeLines = activationLines(cart);
  const currentQuantity = feeLines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const alreadyClean =
    (required === 0 && feeLines.length === 0) ||
    (required > 0 && feeLines.length === 1 && currentQuantity === required);

  if (alreadyClean) return cart;

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ merchandiseId: activationVariantId, quantity: required }],
      promise: deferred.promise,
    })
  );

  try {
    if (feeLines.length === 1) {
      const line = feeLines[0];
      if (Number(line.quantity || 0) !== required) {
        await changeLine(line.key, required);
      }
    } else {
      for (const line of feeLines) {
        await changeLine(line.key, 0);
      }
      if (required > 0) await addActivation(required);
    }

    const updatedCart = await fetchCart();
    applyCartUi(updatedCart);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
      detail: {
        items: updatedCart.items,
        itemCount: updatedCart.item_count,
        source: 'spark-internet-cart',
        didError: false,
      },
    });
    return updatedCart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

function reconcile() {
  if (currentReconcile) {
    rerunRequested = true;
    return currentReconcile;
  }

  currentReconcile = performReconcile()
    .catch((error) => {
      console.error('[SPARK Internet] Cart reconciliation failed', error);
      document.dispatchEvent(
        new CartErrorEvent({
          error: 'We could not apply the required Internet activation fee. Please try again.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      throw error;
    })
    .finally(() => {
      currentReconcile = null;
      if (rerunRequested) {
        rerunRequested = false;
        queueMicrotask(() => reconcile().catch(() => {}));
      }
    });

  return currentReconcile;
}

if (activationVariantId && planVariantIds.size > 0) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (currentReconcile) return;
    event.promise
      ?.then(({ detail }) => {
        if (detail?.source === 'spark-internet-cart') return;
        return reconcile();
      })
      .catch(() => {});
  });

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
      if (!(submitter instanceof HTMLButtonElement) || submitter.name !== 'checkout') return;

      if (checkoutBypass) {
        checkoutBypass = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      try {
        await reconcile();
        checkoutBypass = true;
        form.requestSubmit(submitter);
      } catch (_) {
        checkoutBypass = false;
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => reconcile().catch(() => {}), { once: true });
  } else {
    reconcile().catch(() => {});
  }
}
